// @ts-nocheck
import React, { useEffect, useState } from "react";
import SpeechButton from "./SpeechButton";
import { AvatarCircles } from "./ui/AvatarCircles";
import { AnimatePresence, motion } from "framer-motion";
import ReactDOM from "react-dom";
import { ChevronDown, ChevronUp } from "lucide-react";

const BotResponse = ({
  response,
  chatLogRef,
  isNewMessage = false,
  isLoading = false,
  role = "assistant",
  isSpeakerDisabled,
  citations
}) => {
  const [formattedContent, setFormattedContent] = React.useState("");

  // Function to escape HTML characters
  const escapeHtml = (unsafe) => {
    return unsafe
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  };

  useEffect(() => {
    const placeholders = document.querySelectorAll(".think-placeholder");

    placeholders.forEach((el) => {
      const content = decodeURIComponent(el.getAttribute("data-think") || "");
      const root = ReactDOM.createRoot(el);
      root.render(<ThinkBlock content={content} />);
    });
  }, [formattedContent]);

  const formatText = (text) => {
    if (!text) return "";

    // Process headings with improved regex
    let formattedText = text.replace(
      /^(#{1,6})\s+(.+)$/gm,
      (match, hashes, content) => {
        const level = hashes.length;
        return `<h${level} class="markdown-heading">${content}</h${level}>`;
      }
    );

    formattedText = formattedText.replace(/\[(\d+)\]/g, "");

    formattedText = formattedText.replace(/#+/g, "\n");

    // Process chart blocks
    formattedText = formattedText.replace(
      /```chart\|(pie|bar)\n([\s\S]*?)```/g,
      (match, chartType, chartData) => {
        try {
          const data = JSON.parse(chartData);
          if (chartType === "pie") {
            return createPieChartHTML(data);
          } else if (chartType === "bar") {
            return createBarChartHTML(data);
          }
          return "<p>Unsupported chart type</p>";
        } catch (e) {
          return `<p>Error parsing chart data: ${e.message}</p>`;
        }
      }
    );

    formattedText = formattedText.replace(
      /<think>([\s\S]*?)<\/think>/g,
      (_, thinkContent) => {
        const encodedContent = encodeURIComponent(thinkContent.trim());
        return `<span data-think="${encodedContent}" class="think-placeholder"></span>`;
      }
    );

    // Process code blocks - with HTML escaping
    formattedText = formattedText.replace(
      /```([\w-]*)\n([\s\S]*?)```/g,
      (match, language, codeContent) => {
        // Escape HTML tags inside code blocks to prevent rendering
        const escapedCode = escapeHtml(codeContent);
        return `<pre><code class="language-${language}">${escapedCode}</code></pre>`;
      }
    );

    // Process inline code - with HTML escaping
    formattedText = formattedText.replace(
      /`([^`]+)`/g,
      (match, content) => `<code>${escapeHtml(content)}</code>`
    );

    // Process bold text
    formattedText = formattedText.replace(
      /\*\*([^*]+)\*\*/g,
      "<strong>$1</strong>"
    );

    // Process italic text
    formattedText = formattedText.replace(/\*([^*]+)\*/g, "<em>$1</em>");

    // Process tables
    formattedText = formattedText.replace(
      /\|(.+)\|\n\|([-:]+\|)+\n((?:\|.+\|\n)+)/g,
      (match, header, separator, rows) => {
        const headers = header
          .split("|")
          .map((h) => h.trim())
          .filter(Boolean);
        const alignments = separator
          .split("|")
          .map((s) => {
            s = s.trim();
            if (s.startsWith(":") && s.endsWith(":")) return "center";
            if (s.endsWith(":")) return "right";
            return "left";
          })
          .filter(Boolean);

        const rowsArray = rows
          .trim()
          .split("\n")
          .map((row) =>
            row
              .split("|")
              .map((cell) => cell.trim())
              .filter(Boolean)
          );

        let tableHTML = '<table class="markdown-table"><thead><tr>';

        // Add headers
        headers.forEach((h, i) => {
          const align = alignments[i] || "left";
          tableHTML += `<th style="text-align: ${align}">${h}</th>`;
        });

        tableHTML += "</tr></thead><tbody>";

        // Add rows
        rowsArray.forEach((row) => {
          tableHTML += "<tr>";
          row.forEach((cell, i) => {
            const align = alignments[i] || "left";
            tableHTML += `<td style="text-align: ${align}">${cell}</td>`;
          });
          tableHTML += "</tr>";
        });

        tableHTML += "</tbody></table>";
        return tableHTML;
      }
    );

    // Process lists
    formattedText = formattedText.replace(
      /(?:^|\n)((?:[ ]*[-*+][ ].+\n?)+)/g,
      (match, list) => {
        const items = list.split("\n").filter((item) => item.trim());
        const listItems = items
          .map((item) => `<li>${item.replace(/^[ ]*[-*+][ ]/, "")}</li>`)
          .join("");
        return `<ul>${listItems}</ul>`;
      }
    );

    // Process numbered lists
    formattedText = formattedText.replace(
      /(?:^|\n)((?:[ ]*\d+\.[ ].+\n?)+)/g,
      (match, list) => {
        const items = list.split("\n").filter((item) => item.trim());
        const listItems = items
          .map((item) => `<li>${item.replace(/^[ ]*\d+\.[ ]/, "")}</li>`)
          .join("");
        return `<ol>${listItems}</ol>`;
      }
    );

    // Process paragraphs (must come last)
    formattedText = formattedText.replace(/(?:\r?\n){2,}/g, "</p><p>");

    // Wrap in paragraph if not already wrapped
    if (!formattedText.startsWith("<")) {
      formattedText = `<p>${formattedText}</p>`;
    }

    return formattedText;
  };

  // Create HTML for a pie chart
  // @ts-ignore
  const createPieChartHTML = (data) => {
    if (!data.series || !data.series[0] || !data.series[0].data) {
      return "<p>Invalid pie chart data format</p>";
    }

    const items = data.series[0].data;
    // @ts-ignore
    const total = items.reduce((sum, item) => sum + item.data, 0);
    const colors = [
      "#FF6384",
      "#36A2EB",
      "#FFCE56",
      "#4BC0C0",
      "#9966FF",
      "#FF9F40",
    ];

    // Calculate each segment's angle
    let startAngle = 0;
    // @ts-ignore
    const segments = items.map((item, index) => {
      const angle = (item.data / total) * 360;
      const segment = {
        name: item.name,
        value: item.data,
        percent: Math.round((item.data / total) * 100),
        color: colors[index % colors.length],
        startAngle,
        endAngle: startAngle + angle,
      };
      startAngle += angle;
      return segment;
    });

    let html = `
      <div class="simple-chart pie-chart">
        <div class="pie-container" style="background: conic-gradient(
    `;

    // Add the conic gradient segments
    // @ts-ignore
    segments.forEach((segment, index) => {
      html += `${segment.color} ${segment.startAngle}deg ${segment.endAngle
        }deg${index < segments.length - 1 ? "," : ""}`;
    });

    html += `);">`;
    html += `</div>`;

    // Add legend
    html += `<div class="chart-legend">`;

    // @ts-ignore
    segments.forEach((segment) => {
      html += `
        <div class="legend-item">
          <span class="color-box" style="background-color: ${segment.color}"></span>
          <span>${segment.name}: ${segment.percent}% (${segment.value})</span>
        </div>
      `;
    });

    html += `</div></div>`;
    return html;
  };

  // Create HTML for a bar chart
  // @ts-ignore
  const createBarChartHTML = (data) => {
    if (!data.labels || !data.series) {
      return "<p>Invalid bar chart data format</p>";
    }

    const labels = data.labels;
    const series = data.series;
    const colors = [
      "#FF6384",
      "#36A2EB",
      "#FFCE56",
      "#4BC0C0",
      "#9966FF",
      "#FF9F40",
    ];

    // Find the maximum value for scaling
    // @ts-ignore
    const maxValue = Math.max(...series.flatMap((s) => s.data));

    let html = `
      <div class="simple-chart bar-chart">
        <div class="bars-container">
    `;

    // Create bars for each label
    // @ts-ignore
    labels.forEach((label, labelIndex) => {
      html += `<div class="bar-group">`;

      // Add each series bar for this label
      // @ts-ignore
      series.forEach((s, seriesIndex) => {
        const value = s.data[labelIndex] || 0;
        const height = Math.max(5, (value / maxValue) * 150); // Min height of 5px
        const color = colors[seriesIndex % colors.length];

        html += `
          <div class="bar" style="height: ${height}px; background-color: ${color};" title="${s.name}: ${value}">
            <span class="bar-value">${value}</span>
          </div>
        `;
      });

      html += `<div class="bar-label">${label}</div></div>`;
    });

    // Add legend
    html += `</div><div class="chart-legend">`;

    // @ts-ignore
    series.forEach((s, index) => {
      const color = colors[index % colors.length];

      html += `
        <div class="legend-item">
          <span class="color-box" style="background-color: ${color}"></span>
          <span>${s.name}</span>
        </div>
      `;
    });

    html += `</div></div>`;
    return html;
  };

  const extractTextFromHTML = (html) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || "";
  };

  // Process response changes
  React.useEffect(() => {
    if (response) {
      const result = formatText(response);
      setFormattedContent(result);

      // Add console logs to help debug
      if (response.includes("```chart|pie")) {
        console.log("Pie chart detected in response");
      }
    }
  }, [response]);

  // Show loading indicator if we're loading and there's no content yet
  if (isLoading && !response) {
    return (
      <div className="botResponse">
        <span className="loading-indicator">Thinking</span>
        <span className="loading-dots">
          <span className="dot">.</span>
          <span className="dot">.</span>
          <span className="dot">.</span>
        </span>
      </div>
    );
  }

  // Skip rendering if this is a system message - do this AFTER hooks
  if (role === "system") {
    return null;
  }

  return (
    <div className="botResponse">
      <div
        className="response-content"
        dangerouslySetInnerHTML={{ __html: formattedContent }}
      />
      {/* Speech Button */}
      {response && (
        <div className="flex items-center mt-2">
          <SpeechButton
            text={extractTextFromHTML(response)}
            disabled={isSpeakerDisabled}
          />
          {
            isNewMessage && citations.length > 0 && <div className="flex gap-2 w-full justify-end p-2">
              <p className="font-semibold">Sources :</p>
              <AvatarCircles avatarUrls={citations.map(url => {
                return {
                  imageUrl: `https://www.google.com/s2/favicons?sz=64&domain_url=${url}`,
                  profileUrl: url
                }
              })} />
            </div>
          }
        </div>
      )}
    </div>
  );
};

const ThinkBlock = ({ content }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="bg-gray-100 px-4 py-2 mb-4 text-sm border-l-4 border-gray-400 text-left rounded-md">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="font-semibold text-left flex justify-between w-full cursor-pointer select-none focus:outline-none"
      >
        Thoughts... {isOpen ? <ChevronUp /> : <ChevronDown />}
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden mt-2 whitespace-pre-line"
          >
            {content}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BotResponse;
